import React, { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function LoginPage({ onAuth, ageDeclined = false }) {
  const [mode, setMode]       = useState("login"); // "login" | "register" | "pending"
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendDone, setResendDone]     = useState(false);

  // Show success banner when redirected from email verification
  const [verifiedBanner] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("verified") === "true";
  });

  // Pre-fill email if coming from verification redirect
  useEffect(() => {
    const savedEmail = sessionStorage.getItem("arvay_verified_email");
    if (savedEmail) {
      setEmail(savedEmail);
      sessionStorage.removeItem("arvay_verified_email");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const ep = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login"
        ? { email: email.trim().toLowerCase(), password }
        : { email: email.trim().toLowerCase(), password, name: name.trim() };
      const res = await axios.post(`${API}${ep}`, body, { withCredentials: true });
      if (res.data.status === "pending") {
        setPendingEmail(email.trim().toLowerCase());
        setMode("pending");
      } else {
        // Store JWT for cross-browser session persistence (Safari ITP, Firefox ETP)
        if (res.data.token) {
          localStorage.setItem("arvay_token", res.data.token);
        }
        onAuth(res.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail === "email_not_verified") {
        setPendingEmail(email.trim().toLowerCase());
        setMode("pending");
      } else {
        setError(detail || "Something went wrong. Please try again.");
      }
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: pendingEmail, password }, { withCredentials: true });
      setResendDone(true);
    } catch { setResendDone(true); } // silently succeed
    finally { setLoading(false); }
  };

  // ── Pending verification state ──
  if (mode === "pending") {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
        <h1 className="text-5xl tracking-[0.25em] uppercase font-light text-[#1A1A1A] mb-12">Arvay</h1>
        <div className="w-full max-w-sm bg-white border border-[#E8E8E4] shadow-sm p-8 text-center">
          <div className="w-10 h-10 border-2 border-black rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </div>
          <h2 className="text-xl font-light tracking-wider mb-2">Check your inbox</h2>
          <p className="font-sans text-xs text-neutral-500 mb-1">We sent a verification link to</p>
          <p className="font-sans text-xs font-semibold text-black mb-6">{pendingEmail}</p>
          <p className="font-sans text-xs text-neutral-400 mb-6">Click the link in the email to activate your account. It expires in 24 hours.</p>
          {resendDone ? (
            <p className="font-sans text-xs text-green-600 mb-4">Verification email resent!</p>
          ) : (
            <button onClick={handleResend} disabled={loading}
              className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold text-neutral-500 hover:text-black underline disabled:opacity-50">
              {loading ? "Sending…" : "Resend email"}
            </button>
          )}
          <div className="mt-4 pt-4 border-t border-[#E0E0E0]">
            <button onClick={() => { setMode("login"); setError(""); }}
              className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold text-neutral-400 hover:text-black">
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="login-page"
      className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center px-4"
      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      {/* Wordmark */}
      <div className="mb-12 text-center">
        <h1 className="text-5xl tracking-[0.25em] uppercase font-light text-[#1A1A1A] mb-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Arvay</h1>
        <p className="font-sans text-[10px] text-neutral-400 leading-relaxed max-w-xs mx-auto mt-2">
          Read between the digital lines — your personal media monitoring feed, news, markets, and trends in one place.
        </p>
      </div>

      {/* Age-decline notice */}
      {ageDeclined && (
        <div
          className="w-full max-w-sm mb-4 border border-[#D4B896] bg-[#FDF8F3] px-5 py-4"
          data-testid="age-declined-notice"
        >
          <p className="font-sans text-xs text-[#7A5C3E] leading-relaxed">
            Arvay is restricted to users aged 17 and above.
          </p>
        </div>
      )}

      {/* Email verified success banner */}
      {verifiedBanner && (
        <div className="w-full max-w-sm mb-4 bg-emerald-50 border border-emerald-200 px-5 py-4 flex items-start gap-3" data-testid="verified-banner">
          <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <div>
            <p className="font-sans text-xs font-semibold text-emerald-800">Email verified successfully</p>
            <p className="font-sans text-[11px] text-emerald-700 mt-0.5">Your account is now active. Please sign in below.</p>
          </div>
        </div>
      )}

      <div className="w-full max-w-sm bg-white border border-[#E8E8E4] shadow-sm p-8">
        {/* Mode toggle */}
        <div className="flex border border-[#E0E0E0] mb-7 overflow-hidden">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }}
              data-testid={`auth-mode-${m}`}
              className={`flex-1 py-2.5 font-sans text-[10px] uppercase tracking-[0.18em] font-semibold transition-all duration-200 ${mode===m?"bg-black text-white":"text-neutral-500 hover:text-black"}`}>
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="font-sans text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold block mb-1.5">Full Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} required
                autoComplete="name" data-testid="auth-name-input" placeholder="Your name"
                className="w-full border border-[#E0E0E0] bg-[#FAFAF8] px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-black transition-colors placeholder:text-neutral-300"/>
            </div>
          )}
          <div>
            <label className="font-sans text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold block mb-1.5">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
              autoComplete="email" data-testid="auth-email-input" placeholder="your@email.com"
              className="w-full border border-[#E0E0E0] bg-[#FAFAF8] px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-black transition-colors placeholder:text-neutral-300"/>
          </div>
          <div>
            <label className="font-sans text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold block mb-1.5">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
              autoComplete={mode==="login"?"current-password":"new-password"}
              data-testid="auth-password-input"
              placeholder={mode==="register"?"Min. 6 characters":"••••••••"}
              className="w-full border border-[#E0E0E0] bg-[#FAFAF8] px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-black transition-colors placeholder:text-neutral-300"/>
          </div>
          {mode === "login" && (
            <div className="text-right">
              <a href="/forgot-password" data-testid="forgot-password-link"
                className="font-sans text-[10px] text-neutral-400 hover:text-black uppercase tracking-[0.12em] font-semibold">
                Forgot password?
              </a>
            </div>
          )}
          {error && <p data-testid="auth-error" className="font-sans text-xs text-red-600 mt-1">{error}</p>}
          <button type="submit" disabled={loading} data-testid="auth-submit-btn"
            className="w-full mt-2 bg-black text-white font-sans text-[10px] uppercase tracking-[0.2em] font-semibold py-3.5 hover:bg-neutral-800 transition-colors disabled:opacity-50 focus:outline-none">
            {loading ? "Please wait…" : mode==="login"?"Sign In":"Create Account"}
          </button>
        </form>

        {/* Mandatory copy */}
        <p className="font-sans text-[10px] text-neutral-400 leading-relaxed mt-6 text-center">
          We only ask you to log in so we can keep the platform safe and personalised for you.
          Core features are free — no card required.
        </p>

        {/* Terms link */}
        <p className="font-sans text-[10px] text-neutral-300 text-center mt-2">
          By continuing you accept our <a href="/terms" target="_blank" className="underline hover:text-neutral-500">Terms of Use</a>
        </p>
      </div>

      <p className="font-sans text-[9px] text-neutral-300 mt-8 uppercase tracking-[0.15em]">
        &copy; {new Date().getFullYear()} Arvay. All rights reserved.
      </p>
    </div>
  );
}
